import type { ReactNode } from "react";
import { SiteHeader } from "./site-header";
import { SiteFooter } from "./site-footer";
import { ScrollToTop } from "./scroll-to-top";

export function PageShell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col">
      <ScrollToTop />
      <SiteHeader />
      <main className="page-enter flex-1">{children}</main>
      <SiteFooter />
    </div>
  );
}

export function PageHero({
  title,
  subtitle,
  image,
  eyebrow,
  children,
}: {
  title: string;
  subtitle?: string;
  image?: string;
  eyebrow?: string;
  children?: ReactNode;
}) {
  return (
    <section className="bg-hero-gradient relative overflow-hidden text-primary-foreground shadow-[0_8px_24px_-8px_rgba(0,0,0,0.18)]">
      {image && (
        <>
          <img
            src={image}
            alt=""
            aria-hidden
            loading="eager"
            className="anim-bg-settle absolute inset-0 h-full w-full object-cover opacity-30"
            style={{ animationName: "jd-bg-settle-30" }}
          />
          <div className="from-primary/80 via-primary/70 to-primary/85 absolute inset-0 bg-gradient-to-b" />
        </>
      )}
      <div className="relative mx-auto max-w-5xl px-4 py-12 text-center sm:py-16 md:py-20">
        {eyebrow && (
          <span
            className="bg-gold/20 anim-fade-up inline-block rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-gold"
            style={{ animationDelay: "100ms" }}
          >
            {eyebrow}
          </span>
        )}
        <h1 className="mt-3 font-serif text-3xl font-bold drop-shadow sm:text-4xl md:text-5xl">
          <span className="anim-line" style={{ animationDelay: "220ms" }}>
            <span>{title}</span>
          </span>
        </h1>
        {subtitle && (
          <p
            className="text-primary-foreground/90 anim-fade-up mx-auto mt-4 max-w-2xl text-sm sm:text-base"
            style={{ animationDelay: "420ms" }}
          >
            {subtitle}
          </p>
        )}
        <div
          className="anim-draw-x mx-auto mt-6 h-px w-24 bg-gold"
          style={{ animationDelay: "560ms", transformOrigin: "center" }}
        />
        {children && (
          <div className="anim-fade-up" style={{ animationDelay: "680ms" }}>
            {children}
          </div>
        )}
      </div>
    </section>
  );
}
